import React, { useState, useEffect } from 'react';
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
    FolderPlus,
    Trash2,
    Edit3,
    ChevronDown,
    ChevronRight,
    Plus,
    Search,
    Save,
    X
} from 'react-feather';
import { Container, Card, Form, Button, Table, InputGroup, Modal } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import './Categories.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

const Categories = () => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [newCategory, setNewCategory] = useState({
        name: '',
        description: '',
        image: null
    });
    const [editingCategory, setEditingCategory] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

    const transformCategory = (cat, level = 0) => {
        return {
            ...cat,
            image: cat.imageName ? `${apiBaseUrl}/public/images/${cat.imageName}` : null,
            level,
            subcategories: cat.subCatalogs ? cat.subCatalogs.map(sub => transformCategory(sub, level + 1)) : []
        };
    };

    const fetchCategoriesRecursive = async (categoryId = null, level = 0) => {
        try {
            const endpoint = categoryId
                ? `${apiBaseUrl}/public/catalogs/${categoryId}/get`
                : `${apiBaseUrl}/public/categories`;
            const response = await axios.get(endpoint);
            let cats = response.data;
            cats = Array.isArray(cats) ? cats : [cats];
            return cats.map(cat => transformCategory(cat, level));
        } catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    };

    const fetchCategories = async () => {
        setIsLoading(true);
        try {
            const cats = await fetchCategoriesRecursive();
            setCategories(cats);
        } catch (error) {
            console.error('Error fetching catalogs:', error);
            Notify.failure(t('categories.errors.fetchFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        setExpandedCategories(new Set());
    }, [categories]);

    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategory.name || !newCategory.description || !newCategory.image) {
            Notify.failure(t('categories.errors.fillAllFields'));
            return;
        }
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                Notify.failure(t('categories.errors.tokenNotFound'));
                return;
            }
            const formData = new FormData();
            formData.append('name', newCategory.name);
            formData.append('description', newCategory.description);
            formData.append('image', newCategory.image);

            let response;
            if (selectedParentId) {
                response = await axios.post(
                    `${apiBaseUrl}/manager/under-catalogs/${selectedParentId}/create`,
                    formData,
                    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' } }
                );
            } else {
                response = await axios.post(
                    `${apiBaseUrl}/manager/catalogs/create`,
                    formData,
                    { headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'multipart/form-data' } }
                );
            }
            await fetchCategories();
            setNewCategory({ name: '', description: '', image: null });
            setSelectedParentId('');
            Notify.success(t('categories.success.added'));
        } catch (error) {
            console.error('Error adding category:', error);
            Notify.failure(t('categories.errors.addFailed'));
        }
    };

    const handleEditClick = (category) => {
        setEditingCategory({
            ...category,
            parentId: category.parentCatalog?.id || ''
        });
        setShowEditModal(true);
    };

    const handleEditCatalog = async () => {
        try {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                Notify.failure(t('categories.errors.tokenNotFound'));
                return;
            }
            const formData = new FormData();
            formData.append("name", editingCategory.name);
            formData.append("description", editingCategory.description);
            if (editingCategory.image instanceof File) {
                formData.append("image", editingCategory.image);
            }
            await axios.put(
                `${apiBaseUrl}/manager/catalog/${editingCategory.id}/update`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            await fetchCategories();
            setShowEditModal(false);
            setEditingCategory(null);
            Notify.success(t('categories.success.updated'));
        } catch (error) {
            console.error("Error updating category:", error);
            Notify.failure(t('categories.errors.updateFailed'));
        }
    };

    const handleDeleteCategory = async (catalogId) => {
        try {
            const accessToken = sessionStorage.getItem('accessToken');
            if (!accessToken) {
                Notify.failure(t('categories.errors.tokenNotFound'));
                return;
            }
            await axios.delete(`${apiBaseUrl}/manager/catalogs/${catalogId}/delete`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            setCategories(prev => {
                const remove = (cats) =>
                    cats.filter(cat => cat.id !== catalogId)
                        .map(cat => ({ ...cat, subcategories: cat.subcategories ? remove(cat.subcategories) : [] }));
                return remove(prev);
            });
            Notify.success(t('categories.success.deleted'));
        } catch (error) {
            console.error('Error deleting category:', error);
            Notify.failure(t('categories.errors.deleteFailed'));
        }
    };

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const getFlattenedCategories = (cats, parentExpanded = true) => {
        let result = [];
        cats.forEach(category => {
            if (!category) return;
            const isExpanded = expandedCategories.has(category.id);
            const matchesSearch = category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                category.description?.toLowerCase().includes(searchTerm.toLowerCase());
            if (parentExpanded && matchesSearch) {
                result.push(category);
            }
            if (isExpanded && category.subcategories?.length > 0) {
                result = result.concat(getFlattenedCategories(category.subcategories, isExpanded));
            }
        });
        return result;
    };

    const renderCategoryRow = (category, index, level = 0) => {
        if (!category) return null;
        const isExpanded = expandedCategories.has(category.id);
        const hasSubcategories = category.subcategories && category.subcategories.length > 0;
        const indentation = level * 24;
        return (
            <tr key={category.id} className={`category-row ${isExpanded ? 'expanded' : ''}`}>
                <td>{index + 1}</td>
                <td>
                    {category.image ? (
                        <img
                            src={category.image}
                            alt={category.name}
                            style={{width: '50px', height: '50px', objectFit: 'cover'}}
                        />
                    ) : '-'}
                </td>
                <td>
                    <div className="d-flex align-items-center category-name">
                        <div style={{width: `${indentation}px`}} className="nested-line-container">
                            {level > 0 && <div className="nested-line"/>}
                        </div>
                        <button
                            className={`expand-button ${!hasSubcategories ? 'invisible' : ''}`}
                            onClick={() => toggleCategory(category.id)}
                            disabled={!hasSubcategories}
                        >
                            {isExpanded ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                        </button>
                        <span className={`ms-2 ${hasSubcategories ? 'fw-semibold' : ''}`}>
                            {category.name}
                            {hasSubcategories && (
                                <span className="category-count">{category.subcategories.length}</span>
                            )}
                        </span>
                    </div>
                </td>
                <td>{category.description}</td>
                <td>{category.parentCatalog ? category.parentCatalog.name : t('categories.table.noParent')}</td>
                <td>
                    <div>
                        <Button
                            size="xs"
                            onClick={() => handleEditClick(category)}
                            style={{backgroundColor: "#0056B3", border: "#0056B3", marginRight: "10px"}}
                        >
                            {t('actions.edit')}
                        </Button>
                        <Button
                            size="xs"
                            onClick={() => handleDeleteCategory(category.id)}
                            style={{backgroundColor: "red", border: "red", marginRight: "10px"}}
                            disabled={true}
                        >
                            {t('actions.delete')}
                        </Button>
                    </div>
                </td>
            </tr>
        );
    };

    const getAllCategories = () => {
        const uniqueIds = new Set();
        return categories.reduce((acc, category) => {
            if (!uniqueIds.has(category.id)) {
                uniqueIds.add(category.id);
                acc.push(category);
            }
            if (category.subcategories) {
                category.subcategories.forEach(sub => {
                    if (!uniqueIds.has(sub.id)) {
                        uniqueIds.add(sub.id);
                        acc.push(sub);
                    }
                });
            }
            return acc;
        }, []);
    };

    const renderCategoriesTable = () => {
        const visibleCategories = getFlattenedCategories(
            categories.filter(cat => !cat.parentCatalog)
        );
        return (
            <Table hover className="categories-table">
                <thead>
                <tr>
                    <th style={{ width: '5%' }}>{t('categories.table.id')}</th>
                    <th style={{ width: '10%' }}>{t('categories.table.photo')}</th>
                    <th style={{ width: '25%' }}>{t('categories.table.name')}</th>
                    <th style={{ width: '25%' }}>{t('categories.table.description')}</th>
                    <th style={{ width: '15%' }}>{t('categories.table.parentCatalog')}</th>
                    <th style={{ width: '20%' }}>{t('categories.table.actions')}</th>
                </tr>
                </thead>
                <tbody>
                {visibleCategories.map((category, index) => {
                    const level = category.level || 0;
                    return renderCategoryRow(category, index, level);
                })}
                </tbody>
            </Table>
        );
    };

    return (
        <>
            <Helmet>
                <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@600;700&display=swap" rel="stylesheet" />
                <link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
            </Helmet>
            <Container fluid className="py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2 className="page-title">{t('categories.title')}</h2>
                    <div className="d-flex gap-3">
                        <InputGroup className="search-input">
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder={t('categories.searchPlaceholder')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </div>
                </div>

                <Card className="mb-4 add-category-card">
                    <Card.Body>
                        <Card.Title className="d-flex align-items-center mb-4">
                            <Plus className="me-2" size={20} />
                            {t('categories.addNewCatalog')}
                        </Card.Title>
                        <Form onSubmit={handleAddCategory}>
                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group className="mb-3" controlId="categoryName">
                                        <Form.Label>{t('categories.form.catalogName')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={newCategory.name}
                                            onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                                            placeholder={t('categories.form.catalogNamePlaceholder')}
                                            required
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3" controlId="categoryDescription">
                                        <Form.Label>{t('categories.form.description')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={newCategory.description}
                                            onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                                            placeholder={t('categories.form.descriptionPlaceholder')}
                                            required
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3" controlId="categoryImage">
                                        <Form.Label>{t('categories.form.photo')}</Form.Label>
                                        <Form.Control
                                            type="file"
                                            onChange={(e) =>
                                                setNewCategory({ ...newCategory, image: e.target.files[0] })
                                            }
                                            required
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3" controlId="parentCategory">
                                        <Form.Label>{t('categories.form.parentCatalog')}</Form.Label>
                                        <Form.Select
                                            value={selectedParentId}
                                            onChange={(e) => setSelectedParentId(e.target.value)}
                                        >
                                            <option value="">{t('categories.form.noParent')}</option>
                                            {getAllCategories().map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </div>
                            <Button type="submit" style={{ backgroundColor: "#0056B3" }} className="w-100 mt-2">
                                <FolderPlus size={18} className="me-2" />
                                {t('categories.addCatalogButton')}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>

                <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('categories.modal.editTitle')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('categories.form.catalogName')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingCategory?.name || ''}
                                    onChange={(e) =>
                                        setEditingCategory({ ...editingCategory, name: e.target.value })
                                    }
                                    placeholder={t('categories.form.catalogNamePlaceholder')}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('categories.form.description')}</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingCategory?.description || ''}
                                    onChange={(e) =>
                                        setEditingCategory({ ...editingCategory, description: e.target.value })
                                    }
                                    placeholder={t('categories.form.descriptionPlaceholder')}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('categories.form.photo')}</Form.Label>
                                <Form.Control
                                    type="file"
                                    onChange={(e) =>
                                        setEditingCategory({ ...editingCategory, image: e.target.files[0] })
                                    }
                                />
                                {editingCategory?.image && !(editingCategory.image instanceof File) && (
                                    <img
                                        src={editingCategory.image}
                                        alt={editingCategory.name}
                                        style={{ width: '100px', marginTop: '10px' }}
                                    />
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('categories.form.parentCatalog')}</Form.Label>
                                <Form.Select
                                    value={editingCategory?.parentId || ''}
                                    onChange={(e) => setEditingCategory({ ...editingCategory, parentId: e.target.value })}
                                >
                                    <option value="">{t('categories.form.noParent')}</option>
                                    {getAllCategories()
                                        .filter(cat => cat.id !== editingCategory?.id)
                                        .map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                </Form.Select>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            <X size={14} className="me-1" />
                            {t('actions.cancel')}
                        </Button>
                        <Button variant="primary" onClick={handleEditCatalog}>
                            <Save size={14} className="me-1" />
                            {t('actions.save')}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Card className="categories-list-card">
                    <Card.Body>
                        <Card.Title className="mb-4">{t('categories.catalogsList')}</Card.Title>
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">{t('categories.loading')}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                {renderCategoriesTable()}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default Categories;
